import { mapTimeAttackFormat } from "./FsTimeAttackFormat";
import { mapTimeAttackTracks } from "./Track/FsTimeAttackTrack";
import { TimeAttackSet } from "model/Match/Set/TimeAttack/TimeAttackSet";
import { nonUndefined } from "Utility";
import { Scores } from "model/Scores";
import { MatchTeams } from "model/Match/MatchTeams";
import { FsTimeAttackSet } from "db/model/match/Set/TimeAttack/FsTimeAttackSet";
import { mapMatchSetBase } from "../FsMatchSet";
import { calculateFinalPositions, Positions } from "model/Positions";
import { getDefinedRosterIds } from "model/Roster/Roster";

export const mapTimeAttackSet = (
  fsModel: FsTimeAttackSet,
  teams: MatchTeams
): TimeAttackSet | undefined => {
  if (!fsModel.format) {
    return undefined;
  }

  const format = mapTimeAttackFormat(fsModel.format);
  if (!format) {
    return undefined;
  }

  const tracks = mapTimeAttackTracks(
    nonUndefined(fsModel.tracks ?? []),
    teams.rosters,
    teams.scope,
    format.scoring,
    format.driversPerTeam
  );

  const scores = calculateSetScores(
    format.sumTrackScores ?? false,
    nonUndefined(tracks.map((t) => t.scores)),
    nonUndefined(tracks.map((t) => t.finalPositions)),
    fsModel.scoreOverrides,
    getDefinedRosterIds(teams.rosters)
  );

  const isComplete =
    fsModel.overrideComplete ||
    tracks.filter((t) => t.isComplete).length >= format.numTracks;

  const finalPositions = calculateFinalPositions(
    isComplete,
    scores,
    (scores, a, b) =>
      compareScores(
        scores,
        a,
        b,
        !(format.lowerBetter ?? false) && (format.sumTrackScores ?? false)
      )
  );

  return {
    ...mapMatchSetBase(fsModel, isComplete, finalPositions, scores),
    format,
    tracks,
    type: "TimeAttack",
  };
};

// Assumes set score is average of track ranks (as done in Beacon World League)
const calculateSetScores = (
  sumTrackScores: boolean,
  trackScores: Scores[],
  finalPositions: Positions[],
  scoreOverrides: Scores | undefined,
  rosterIds: string[]
) => {
  const scores: Scores = {};
  for (var i = 0; i < rosterIds.length; i++) {
    const rosterId = rosterIds[i];
    const scoreOverride = !!scoreOverrides
      ? scoreOverrides[rosterId]
      : undefined;
    if (scoreOverride !== undefined) {
      scores[rosterId] = scoreOverride;
    } else {
      if (sumTrackScores) {
        const rosterScores = nonUndefined(trackScores.map((r) => r[rosterId]));
        if (rosterScores.length === 0) {
          continue;
        }

        scores[rosterId] = rosterScores.reduce((a, b) => a + b, 0);
      } else {
        const rosterPositions = nonUndefined(
          finalPositions.map((r) => r[rosterId])
        );
        if (rosterPositions.length === 0) {
          continue;
        }

        scores[rosterId] =
          rosterPositions.reduce((a, b) => a + b, 0) / rosterPositions.length;
      }
    }
  }

  return scores;
};

// todo - Apply tiebreakers here
const compareScores = (
  scores: Scores,
  a: string,
  b: string,
  biggerBetter: boolean
) => {
  if (biggerBetter) {
    return scores[a] - scores[b];
  }

  return scores[b] - scores[a];
};
